import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { useStockEntryPipelinePaged } from "../../hooks/useSowingLifecycle";
import type { StockEntryPipelineDto } from "../../services/sowingLifecycleApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AcknowledgeStockEntryDialog } from "./AcknowledgeStockEntryDialog";
import { MarkEntryReadyDialog } from "./MarkEntryReadyDialog";
import { RequestTransferDialog } from "./RequestTransferDialog";
import { StockSowingDate } from "../greenhouseStock/StockSowingDate";

const fmt = (d?: string | null) => (d ? format(parseISO(d), "dd MMM yyyy") : "—");
const fmtQty = (n: number) =>
  Number.isInteger(n) ? n.toString() : n.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");

type Stage = "growth" | "mark-ready";

interface Props {
  stage: Stage;
  actionAllowed: boolean;
}

type SortKey =
  | "entryDate-desc"
  | "entryDate-asc"
  | "expectedReady-asc"
  | "expectedReady-desc"
  | "crop-asc"
  | "instructionNumber-asc"
  | "available-desc";

const formatLocation = (e: StockEntryPipelineDto) => {
  if (!e.greenhouseId) return null;
  const gh = e.greenhouseName ?? "Greenhouse";
  if (!e.bedSummaries || e.bedSummaries.length === 0) return gh;
  // Show first 2 bed summaries, with overflow count if more.
  const shown = e.bedSummaries.slice(0, 2).join(", ");
  const more = e.bedSummaries.length - 2;
  return more > 0 ? `${gh} (${shown}, +${more} more)` : `${gh} (${shown})`;
};

export const StockEntryWorkflowTable = ({ stage, actionAllowed }: Props) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("entryDate-desc");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [ackTarget, setAckTarget] = useState<StockEntryPipelineDto | null>(null);
  const [readyTarget, setReadyTarget] = useState<StockEntryPipelineDto | null>(null);
  const [transferTarget, setTransferTarget] = useState<StockEntryPipelineDto | null>(null);

  // Debounce search input so we don't hammer the server on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Snap back to page 1 when filters/sort/stage change.
  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch, sortKey, stage, pageSize]);

  const state = stage === "mark-ready" ? "growing" : "growth";
  const query = useStockEntryPipelinePaged({
    state,
    pageNumber,
    pageSize,
    search: debouncedSearch || undefined,
    sortBy: sortKey,
  });

  const data = query.data;
  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const showingFrom = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const showingTo = Math.min(pageNumber * pageSize, totalCount);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search instruction #, crop, variety, greenhouse, tray type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md flex-1"
        />
        <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
          <SelectTrigger className="w-full sm:w-[220px]" aria-label="Sort by">
            <SelectValue placeholder="Sort by…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="entryDate-desc">Newest first</SelectItem>
            <SelectItem value="entryDate-asc">Oldest first</SelectItem>
            <SelectItem value="expectedReady-asc">Expected ready (soonest)</SelectItem>
            <SelectItem value="expectedReady-desc">Expected ready (latest)</SelectItem>
            <SelectItem value="crop-asc">Crop / Variety (A–Z)</SelectItem>
            <SelectItem value="instructionNumber-asc">Instruction # (A–Z)</SelectItem>
            <SelectItem value="available-desc">Available (high → low)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {query.isLoading && !data ? (
        <div className="flex items-center justify-center h-32">
          <p className="text-muted-foreground">Loading…</p>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border bg-muted/40 p-8 text-center">
          <p className="text-muted-foreground">
            {stage === "growth"
              ? "No stock entries in the growth phase."
              : "No stock entries currently growing."}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Instruction #</TableHead>
                  <TableHead>Crop</TableHead>
                  <TableHead>Variety</TableHead>
                  <TableHead>Tray Type</TableHead>
                  <TableHead>Greenhouse · Bay / Bed</TableHead>
                  <TableHead>Available</TableHead>
                  <TableHead>Filling Reserve</TableHead>
                  <TableHead>Entry Date</TableHead>
                  <TableHead>Sowing Date</TableHead>
                  <TableHead>Expected Ready</TableHead>
                  {actionAllowed && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => {
                  const needsAcknowledge = !e.greenhouseId;
                  const location = formatLocation(e);
                  return (
                    <TableRow key={e.id}>
                      <TableCell className="font-mono text-sm">{e.instructionNumber}</TableCell>
                      <TableCell>{e.cropTypeName}</TableCell>
                      <TableCell>{e.variety ?? "—"}</TableCell>
                      <TableCell>{e.trayType ?? "—"}</TableCell>
                      <TableCell className="text-sm">
                        {location ?? (
                          <span className="text-amber-700 dark:text-amber-400">Not assigned</span>
                        )}
                      </TableCell>
                      <TableCell className="font-semibold text-green-700 dark:text-green-400">
                        {fmtQty(e.availableQuantity)}
                      </TableCell>
                      <TableCell className="text-amber-700 dark:text-amber-400">
                        {fmtQty(e.fillingReserve)}
                      </TableCell>
                      <TableCell className="text-sm">{fmt(e.entryDate)}</TableCell>
                      <TableCell className="text-sm"><StockSowingDate value={e.sowingDate} /></TableCell>
                      <TableCell className="text-sm">{fmt(e.expectedReadyDate)}</TableCell>
                      {actionAllowed && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {stage === "growth" && needsAcknowledge ? (
                              <Button size="sm" variant="outline" onClick={() => setAckTarget(e)}>
                                Acknowledge
                              </Button>
                            ) : (
                              <>
                                {e.greenhouseId && e.isTrayStock && (
                                  <Button size="sm" variant="outline" onClick={() => setTransferTarget(e)}>
                                    Transfer
                                  </Button>
                                )}
                                <Button size="sm" onClick={() => setReadyTarget(e)}>
                                  Mark Ready
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            <div>
              Showing {showingFrom}–{showingTo} of {totalCount}
              {query.isFetching && !query.isLoading && (
                <span className="ml-2 text-xs opacity-70">refreshing…</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                  className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span>
                Page {pageNumber} of {Math.max(totalPages, 1)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages}
                onClick={() => setPageNumber((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {ackTarget && (
        <AcknowledgeStockEntryDialog
          entry={ackTarget}
          open
          onOpenChange={(o) => !o && setAckTarget(null)}
          onSuccess={() => setAckTarget(null)}
        />
      )}
      {readyTarget && (
        <MarkEntryReadyDialog
          entry={readyTarget}
          open
          onOpenChange={(o) => !o && setReadyTarget(null)}
          onSuccess={() => setReadyTarget(null)}
        />
      )}
      {transferTarget && (
        <RequestTransferDialog
          entry={transferTarget}
          open
          onOpenChange={(o) => !o && setTransferTarget(null)}
          onSuccess={() => setTransferTarget(null)}
        />
      )}
    </div>
  );
};
